import { useEffect, useState } from "react";
import { Search, Plus, Trash2, Save, Send, GripVertical, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Loader, Pill } from "../components/common";

const BLANK = { name: "", objective: "", difficulty: "Intermediate", duration_min: 60, warmup: "5 min dynamic warm-up.", safety_notes: "Stop if you feel sharp pain.", coach_intro: "", exercises: [] };
const WEEKDAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const todayStr = () => new Date().toISOString().slice(0, 10);
const defaultSchedule = () => ({ open: true, customers: [], workout_id: null, start: todayStr(), recur_mode: "weekdays", workout_days: [0, 2, 4], rest_days: [], day_overrides: {}, interval_days: 2, dur_mode: "weeks", weeks: 4, months: 1, end_date: "", rest_fill: false });

export default function WorkoutBuilder() {
  const [exercises, setExercises] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadedId, setLoadedId] = useState(null);
  const [q, setQ] = useState("");
  const [workout, setWorkout] = useState(BLANK);
  const [assign, setAssign] = useState({ open: false, ...defaultSchedule(), open: false });

  const loadTemplates = () => api.get("/workouts").then(({ data }) => setTemplates(data));
  useEffect(() => {
    api.get("/exercises").then(({ data }) => setExercises(data));
    api.get("/coach/customers").then(({ data }) => setCustomers(data));
    loadTemplates();
  }, []);
  if (!exercises) return <Loader />;

  const filtered = exercises.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));

  const addEx = (e) => setWorkout((w) => ({ ...w, exercises: [...w.exercises, { exercise_id: e.id, name: e.name, video_url: e.video_url, thumbnail: e.thumbnail, muscle_groups: e.muscle_groups, equipment: e.equipment, instructions: e.instructions, sets: 3, reps: 10, weight: "", rest: 90, tempo: "", rpe: 7, coach_notes: "" }] }));
  const removeEx = (i) => setWorkout((w) => ({ ...w, exercises: w.exercises.filter((_, idx) => idx !== i) }));
  const updateEx = (i, patch) => setWorkout((w) => ({ ...w, exercises: w.exercises.map((e, idx) => idx === i ? { ...e, ...patch } : e) }));

  const clearBuilder = () => { setWorkout(BLANK); setLoadedId(null); };

  const loadTemplate = (t) => {
    setWorkout({ name: t.name, objective: t.objective || "", difficulty: t.difficulty || "Intermediate", duration_min: t.duration_min || 60, warmup: t.warmup || "", safety_notes: t.safety_notes || "", coach_intro: t.coach_intro || "", exercises: t.exercises || [] });
    setLoadedId(t.id);
    toast.success(`Loaded "${t.name}" — edit or assign it.`);
  };

  const duplicateTemplate = (t) => {
    setWorkout({ name: `${t.name} (Copy)`, objective: t.objective || "", difficulty: t.difficulty || "Intermediate", duration_min: t.duration_min || 60, warmup: t.warmup || "", safety_notes: t.safety_notes || "", coach_intro: t.coach_intro || "", exercises: t.exercises || [] });
    setLoadedId(null);
    toast.success("Copied — save it as a new template.");
  };

  const openScheduler = (wid) => setAssign({ ...defaultSchedule(), workout_id: wid || null });
  const assignTemplate = (t) => openScheduler(t.id);
  const toggleDay = (field, wd) => setAssign((a) => ({ ...a, [field]: a[field].includes(wd) ? a[field].filter((x) => x !== wd) : [...a[field], wd].sort((x, y) => x - y) }));

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Already-assigned workouts are not affected.`)) return;
    try {
      await api.delete(`/workouts/${t.id}`);
      if (loadedId === t.id) clearBuilder();
      await loadTemplates();
      toast.success("Template deleted.");
    } catch (e) { toast.error("Could not delete template."); }
  };

  const save = async () => {
    if (!workout.name) { toast.error("Name your workout."); return null; }
    if (workout.exercises.length === 0) { toast.error("Add at least one exercise."); return null; }
    const { data } = loadedId ? await api.put(`/workouts/${loadedId}`, workout) : await api.post("/workouts", workout);
    setLoadedId(data.id);
    await loadTemplates();
    toast.success(loadedId ? "Template updated!" : "Workout saved as template!");
    return data.id;
  };

  const doAssign = async () => {
    let wid = assign.workout_id;
    if (!wid) wid = await save();
    if (!wid) return;
    if (assign.customers.length === 0) return toast.error("Select at least one customer.");
    if (assign.recur_mode === "weekdays" && assign.workout_days.length === 0) return toast.error("Pick at least one workout day.");
    if (assign.dur_mode === "until" && !assign.end_date) return toast.error("Choose an end date.");
    try {
      const { data } = await api.post("/assignments/schedule", {
        customer_ids: assign.customers,
        default_workout_id: wid,
        start_date: assign.start,
        recur_mode: assign.recur_mode,
        workout_days: assign.workout_days,
        rest_days: assign.rest_days,
        day_overrides: assign.day_overrides,
        interval_days: Number(assign.interval_days) || 1,
        dur_mode: assign.dur_mode,
        weeks: Number(assign.weeks) || 1,
        months: Number(assign.months) || 1,
        end_date: assign.end_date || null,
        rest_fill: assign.rest_fill,
      });
      toast.success(`Scheduled ${data.workouts} workout(s) + ${data.rest_days} rest day(s) for ${assign.customers.length} customer(s).`);
      setAssign((a) => ({ ...a, open: false }));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(", ") : "Could not schedule.");
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-5xl">Workout Builder</h1><p className="text-secondary mt-1">{loadedId ? "Editing a saved template — changes update the template." : "Build it, save it, assign it."}</p></div>
        <div className="flex gap-2">
          <button data-testid="builder-new" onClick={clearBuilder} className="px-5 py-2.5 border border-white/20 rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:border-white"><Plus className="w-4 h-4" /> New</button>
          <button data-testid="builder-save" onClick={save} className="px-5 py-2.5 border border-white/20 rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:border-white"><Save className="w-4 h-4" /> {loadedId ? "Update" : "Save"}</button>
          <button data-testid="builder-assign" disabled={!loadedId && (!workout.name || workout.exercises.length === 0)} onClick={() => openScheduler(loadedId)} className="px-5 py-2.5 bg-brand text-white rounded-lg font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"><Send className="w-4 h-4" /> Schedule</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6 mt-6">
        <div>
          <div className="card-surface rounded-xl p-4 space-y-3">
            <input data-testid="builder-name" value={workout.name} onChange={(e) => setWorkout({ ...workout, name: e.target.value })} placeholder="Workout name" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-3 font-heading text-2xl focus:outline-none focus:border-brand" />
            <input data-testid="builder-objective" value={workout.objective} onChange={(e) => setWorkout({ ...workout, objective: e.target.value })} placeholder="Objective" className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />
            <div className="grid grid-cols-2 gap-3">
              <select value={workout.difficulty} onChange={(e) => setWorkout({ ...workout, difficulty: e.target.value })} className="bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm">{["Beginner", "Intermediate", "Advanced"].map((x) => <option key={x}>{x}</option>)}</select>
              <input type="number" value={workout.duration_min} onChange={(e) => setWorkout({ ...workout, duration_min: Number(e.target.value) })} placeholder="Duration (min)" className="bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm" />
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {workout.exercises.length === 0 && <div className="card-surface rounded-xl p-8 text-center text-tertiary text-sm">Add exercises from the library →</div>}
            {workout.exercises.map((ex, i) => (
              <div key={i} className="card-surface rounded-xl p-3" data-testid={`builder-ex-${i}`}>
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-tertiary" />
                  <span className="font-num text-tertiary text-sm">{i + 1}</span>
                  <p className="font-semibold flex-1">{ex.name}</p>
                  <button data-testid={`builder-remove-${i}`} onClick={() => removeEx(i)} className="text-tertiary hover:text-brand"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {[["sets", "Sets"], ["reps", "Reps"], ["rest", "Rest"], ["rpe", "RPE"]].map(([k, l]) => (
                    <div key={k}><label className="label-xs">{l}</label><input value={ex[k]} onChange={(e) => updateEx(i, { [k]: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-2 py-1.5 text-sm mt-1 focus:outline-none focus:border-brand" /></div>
                  ))}
                </div>
                <input value={ex.coach_notes} onChange={(e) => updateEx(i, { coach_notes: e.target.value })} placeholder="Coach note for this exercise" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm mt-2 focus:outline-none focus:border-brand" />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4 lg:sticky lg:top-6 h-fit">
          <div className="card-surface rounded-xl p-4">
            <p className="label-xs mb-2">Saved Templates ({templates.length})</p>
            {templates.length === 0 ? (
              <p className="text-tertiary text-xs">Build a workout and hit Save to reuse it later.</p>
            ) : (
              <div className="space-y-1.5 max-h-[32vh] overflow-y-auto">
                {templates.map((t) => (
                  <div key={t.id} data-testid={`tpl-${t.id}`} className={`group rounded-lg p-2 ${loadedId === t.id ? "bg-brand/10 border border-brand/40" : "hover:bg-white/5"}`}>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-tertiary flex-shrink-0" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{t.name}</p><p className="text-xs text-tertiary">{(t.exercises || []).length} exercises · {t.difficulty}</p></div>
                    </div>
                    <div className="hidden group-hover:block mt-2 pt-2 border-t border-white/10">
                      <p className="text-[10px] uppercase tracking-wide text-tertiary mb-1">Exercises</p>
                      {(t.exercises || []).length === 0 ? (
                        <p className="text-xs text-tertiary">No exercises in this template.</p>
                      ) : (
                        <ul className="text-xs text-secondary space-y-0.5 max-h-32 overflow-y-auto">
                          {t.exercises.map((e, i) => (
                            <li key={i} className="flex justify-between gap-2"><span className="truncate">{i + 1}. {e.name}</span><span className="text-tertiary whitespace-nowrap">{e.sets}×{e.reps}</span></li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <button data-testid={`tpl-assign-${t.id}`} onClick={() => assignTemplate(t)} className="flex-1 text-xs py-1.5 bg-brand text-white rounded-md font-semibold flex items-center justify-center gap-1"><Send className="w-3 h-3" /> Assign</button>
                      <button data-testid={`tpl-load-${t.id}`} onClick={() => loadTemplate(t)} className="px-2 py-1.5 border border-white/15 rounded-md text-xs hover:border-white" title="Load to edit">Edit</button>
                      <button data-testid={`tpl-copy-${t.id}`} onClick={() => duplicateTemplate(t)} className="px-2 py-1.5 border border-white/15 rounded-md text-tertiary hover:text-white" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                      <button data-testid={`tpl-delete-${t.id}`} onClick={() => deleteTemplate(t)} className="px-2 py-1.5 border border-white/15 rounded-md text-tertiary hover:text-brand" title="Delete template"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-surface rounded-xl p-4">
            <p className="label-xs mb-2">Exercise Library</p>
            <div className="relative"><Search className="w-4 h-4 text-tertiary absolute left-3 top-1/2 -translate-y-1/2" /><input data-testid="builder-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercises" className="w-full bg-surface-elevated border border-border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-brand" /></div>
            <div className="mt-3 space-y-1.5 max-h-[44vh] overflow-y-auto">
              {filtered.map((e) => (
                <button key={e.id} data-testid={`lib-add-${e.id}`} onClick={() => addEx(e)} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 text-left">
                  <div className="w-9 h-9 rounded-md overflow-hidden bg-white/5 flex-shrink-0"><img src={e.thumbnail} alt="" className="w-full h-full object-cover" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{e.name}</p><p className="text-xs text-tertiary">{e.category}</p></div>
                  <Plus className="w-4 h-4 text-brand" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {assign.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-5 z-50" onClick={() => setAssign((a) => ({ ...a, open: false }))}>
          <div className="card-surface rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-3xl">Schedule Workout</h2>
            <p className="text-secondary text-sm mt-1">Set up recurring sessions across weeks or months, with rest days.</p>

            <label className="label-xs block mt-5 mb-1">Start date</label>
            <input data-testid="assign-start" type="date" value={assign.start} onChange={(e) => setAssign({ ...assign, start: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />

            <label className="label-xs block mt-5 mb-2">Recurrence</label>
            <div className="flex gap-2">
              {[["weekdays", "Days of week"], ["interval", "Every N days"]].map(([m, l]) => (
                <button key={m} data-testid={`recur-${m}`} onClick={() => setAssign({ ...assign, recur_mode: m })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${assign.recur_mode === m ? "bg-brand text-white" : "border border-white/15 text-secondary"}`}>{l}</button>
              ))}
            </div>

            {assign.recur_mode === "weekdays" ? (
              <>
                <label className="label-xs block mt-4 mb-2">Workout days</label>
                <div className="flex gap-1.5 flex-wrap">
                  {WEEKDAYS.map(([lbl, wd]) => (
                    <button key={wd} data-testid={`wkday-${wd}`} onClick={() => toggleDay("workout_days", wd)} className={`w-11 py-2 rounded-lg text-xs font-semibold ${assign.workout_days.includes(wd) ? "bg-brand text-white" : "border border-white/15 text-secondary"}`}>{lbl}</button>
                  ))}
                </div>

                {assign.workout_days.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    <p className="text-tertiary text-xs">Workout per day (defaults to the current workout)</p>
                    {assign.workout_days.map((wd) => (
                      <div key={wd} className="flex items-center gap-2">
                        <span className="text-sm w-10">{WEEKDAYS.find(([, n]) => n === wd)[0]}</span>
                        <select data-testid={`override-${wd}`} value={assign.day_overrides[String(wd)] || ""} onChange={(e) => setAssign((a) => ({ ...a, day_overrides: { ...a.day_overrides, [String(wd)]: e.target.value } }))} className="flex-1 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand">
                          <option value="">Same as default</option>
                          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}

                <label className="label-xs block mt-4 mb-2">Rest days <span className="text-tertiary normal-case">(shown as Rest Day cards)</span></label>
                <div className="flex gap-1.5 flex-wrap">
                  {WEEKDAYS.map(([lbl, wd]) => (
                    <button key={wd} data-testid={`restday-${wd}`} disabled={assign.workout_days.includes(wd)} onClick={() => toggleDay("rest_days", wd)} className={`w-11 py-2 rounded-lg text-xs font-semibold disabled:opacity-30 ${assign.rest_days.includes(wd) ? "bg-white/70 text-black" : "border border-white/15 text-secondary"}`}>{lbl}</button>
                  ))}
                </div>
              </>
            ) : (
              <div className="mt-4 flex items-center gap-2">
                <span className="text-sm">Repeat every</span>
                <input data-testid="interval-days" type="number" min="1" value={assign.interval_days} onChange={(e) => setAssign({ ...assign, interval_days: e.target.value })} className="w-20 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:border-brand" />
                <span className="text-sm">day(s)</span>
              </div>
            )}

            <label className="label-xs block mt-5 mb-2">Duration</label>
            <div className="flex gap-2">
              {[["weeks", "Weeks"], ["months", "Months"], ["until", "Until date"]].map(([m, l]) => (
                <button key={m} data-testid={`dur-${m}`} onClick={() => setAssign({ ...assign, dur_mode: m })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${assign.dur_mode === m ? "bg-brand text-white" : "border border-white/15 text-secondary"}`}>{l}</button>
              ))}
            </div>
            <div className="mt-2">
              {assign.dur_mode === "weeks" && <div className="flex items-center gap-2"><input data-testid="dur-weeks-input" type="number" min="1" value={assign.weeks} onChange={(e) => setAssign({ ...assign, weeks: e.target.value })} className="w-20 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-center" /><span className="text-sm text-secondary">week(s)</span></div>}
              {assign.dur_mode === "months" && <div className="flex items-center gap-2"><input data-testid="dur-months-input" type="number" min="1" value={assign.months} onChange={(e) => setAssign({ ...assign, months: e.target.value })} className="w-20 bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-center" /><span className="text-sm text-secondary">month(s)</span></div>}
              {assign.dur_mode === "until" && <input data-testid="dur-enddate" type="date" value={assign.end_date} onChange={(e) => setAssign({ ...assign, end_date: e.target.value })} className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-brand" />}
            </div>

            <label className="flex items-center gap-2 mt-4 cursor-pointer">
              <input data-testid="rest-fill" type="checkbox" checked={assign.rest_fill} onChange={(e) => setAssign({ ...assign, rest_fill: e.target.checked })} className="accent-[#FF3B30] w-4 h-4" />
              <span className="text-sm text-secondary">Fill all other days as Rest Days</span>
            </label>

            <label className="label-xs block mt-5 mb-2">Customers</label>
            {customers.length === 0 ? (
              <p className="text-tertiary text-sm">No customers yet.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {customers.map((c) => (
                  <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
                    <input data-testid={`assign-cust-${c.id}`} type="checkbox" checked={assign.customers.includes(c.id)} onChange={(e) => setAssign((a) => ({ ...a, customers: e.target.checked ? [...a.customers, c.id] : a.customers.filter((x) => x !== c.id) }))} className="accent-[#FF3B30] w-4 h-4" />
                    <div className="w-8 h-8 rounded-full bg-brand/20 flex items-center justify-center font-heading text-brand text-sm">{c.name[0]}</div>
                    <span className="text-sm">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
            <button data-testid="assign-confirm" onClick={doAssign} className="w-full mt-5 py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover">Create Schedule</button>
          </div>
        </div>
      )}
    </div>
  );
}
